import { Button } from '@cortex/web-design-system/components/ui/Button';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CountedTextField, withinLimit } from './CountedTextField.js';

/**
 * その場で直せる文。編集キーの人が押すと入力欄になり、「保存」で onSave に渡す（空は null）。
 * onSave が true を返したら入力欄を閉じる（保存できなかったら入力を残す）。
 *
 * @graph-node {Function}
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 課題のタイトル・中身・メモなどを、押したその場で直して保存できるようにする。字数の上限を超えたら保存できない
 * @graph-connects CountedTextField [calls] 入力欄
 */
export function InlineTextEditor({
  label,
  value,
  max,
  canEdit,
  onSave,
  children,
  multiline = true,
  required = false,
}: {
  label: string;
  value: string | null;
  max: number;
  canEdit: boolean;
  onSave: (next: string | null) => Promise<boolean>;
  children: ReactNode;
  multiline?: boolean;
  required?: boolean;
}) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  if (draft === null) {
    if (!canEdit) return <>{children}</>;
    return (
      <button
        type="button"
        aria-label={t('issueMgmt.common.editField', { field: label })}
        onClick={() => {
          setDraft(value ?? '');
        }}
        className="block w-full cursor-text rounded-md text-left hover:bg-[var(--color-bg-hover)]"
      >
        {children}
      </button>
    );
  }

  const trimmed = draft.trim();
  const ready =
    withinLimit(draft, max) && (!required || trimmed !== '') && trimmed !== (value ?? '');
  return (
    <div className="space-y-2">
      <CountedTextField
        label={label}
        value={draft}
        onChange={setDraft}
        max={max}
        multiline={multiline}
      />
      <div className="flex justify-end gap-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            setDraft(null);
          }}
        >
          {t('issueMgmt.common.cancel')}
        </Button>
        <Button
          size="sm"
          disabled={!ready}
          loading={saving}
          onClick={() => {
            setSaving(true);
            void onSave(trimmed === '' ? null : trimmed).then((saved) => {
              setSaving(false);
              if (saved) setDraft(null);
            });
          }}
        >
          {t('issueMgmt.common.save')}
        </Button>
      </div>
    </div>
  );
}
